// frontend/src/lib/siteConfigStore.js
import { supabase } from "@/lib/supabase";
import { SITE_SLUG } from "@/config/defaultSiteConfig";
import { defaultConfigForSlug } from "@/config/siteRegistry";

const deepMerge = (defaults, overrides) => {
  if (!overrides || typeof overrides !== "object") return { ...defaults };
  const result = { ...defaults };
  for (const key of Object.keys(overrides)) {
    const val = overrides[key];
    if (val === null || val === undefined) continue;
    if (
      defaults[key] &&
      typeof defaults[key] === "object" &&
      !Array.isArray(defaults[key]) &&
      typeof val === "object" &&
      !Array.isArray(val)
    ) {
      result[key] = deepMerge(defaults[key], val);
    } else {
      result[key] = val;
    }
  }
  return result;
};

export function mergeWithSiteDefaults(slug, overrides) {
  const defaults = defaultConfigForSlug(slug);
  const merged = deepMerge(defaults, overrides);
  if (!Array.isArray(merged.products)) return merged;

  const defaultProductsById = new Map(
    (defaults.products || []).map((product) => [product.id, product])
  );
  const fallbackKind = merged.meta?.allowedKinds?.[0] || "digital";
  merged.products = merged.products.map((product) => {
    const defaultProduct = defaultProductsById.get(product.id);
    return {
      ...product,
      kind: product.kind ?? defaultProduct?.kind ?? fallbackKind,
      format: product.format ?? defaultProduct?.format ?? "",
      storeId: product.storeId ?? defaultProduct?.storeId ?? "",
      checkoutUrl: product.checkoutUrl ?? defaultProduct?.checkoutUrl ?? "",
    };
  });
  return merged;
}

export function storesById(orgConfig) {
  const map = {};
  for (const store of orgConfig?.stores || []) map[store.id] = store;
  return map;
}

export function mergedFormats(orgConfig, siteConfig) {
  const seen = new Map();
  for (const f of [...(orgConfig?.formats || []), ...(siteConfig?.formats || [])]) {
    if (f?.id && !seen.has(f.id)) seen.set(f.id, f);
  }
  return [...seen.values()];
}

// Landing page: anon read from public view
export async function fetchPublished(slug = SITE_SLUG) {
  const defaults = defaultConfigForSlug(slug);
  if (!supabase) return { ...defaults };
  try {
    const { data, error } = await supabase
      .schema("content")
      .from("public_site_config")
      .select("published")
      .eq("id", slug)
      .single();
    if (error || !data?.published) return { ...defaults };
    return mergeWithSiteDefaults(slug, data.published);
  } catch {
    return { ...defaults };
  }
}

// Admin: authenticated read from real table
export async function fetchDraft(slug = SITE_SLUG) {
  if (!supabase) throw new Error("Supabase not configured");
  const { data, error } = await supabase
    .schema("content")
    .from("site_config")
    .select("draft, published, updated_at, published_at")
    .eq("id", slug)
    .single();
  if (error) throw error;
  return {
    draft: mergeWithSiteDefaults(slug, data.draft),
    published: mergeWithSiteDefaults(slug, data.published),
    updatedAt: data.updated_at,
    publishedAt: data.published_at,
  };
}

// Admin: save draft
export async function saveDraft(slug, config) {
  if (!supabase) throw new Error("Supabase not configured");
  const { data, error } = await supabase
    .schema("content")
    .from("site_config")
    .update({ draft: config, updated_at: new Date().toISOString() })
    .eq("id", slug)
    .select("id");
  if (error) throw error;
  if (!data || data.length === 0) throw new Error("Site config row not found — save failed");
}

// Admin: publish (copy draft -> published)
export async function publish(slug) {
  if (!supabase) throw new Error("Supabase not configured");
  const { data, error: fetchError } = await supabase
    .schema("content")
    .from("site_config")
    .select("draft")
    .eq("id", slug)
    .single();
  if (fetchError) throw fetchError;
  if (!data?.draft) throw new Error("No draft to publish");

  const { data: userData } = await supabase.auth.getUser();
  if (!userData?.user) throw new Error("Session expired — please sign in again");

  const { data: updateData, error } = await supabase
    .schema("content")
    .from("site_config")
    .update({
      published: data.draft,
      published_at: new Date().toISOString(),
      published_by: userData.user.id,
    })
    .eq("id", slug)
    .select("id");
  if (error) throw error;
  if (!updateData || updateData.length === 0) throw new Error("Site config row not found — publish failed");
}
